import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Trash2, CheckCircle, XCircle, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('admin_token');

  const fetchTestimonials = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/testimonials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestimonials(res.data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API}/api/admin/testimonials/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTestimonials();
    } catch (err) {
      alert('Failed to approve');
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.put(`${API}/api/admin/testimonials/${id}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTestimonials();
    } catch (err) {
      alert('Failed to reject');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await axios.delete(`${API}/api/admin/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTestimonials();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  if (loading) return <div className="p-8 text-white">Loading...</div>;

  return (
    <div className="p-8" data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-white">Testimonials</h1>
        <span className="text-gray-400 text-sm">{testimonials.length} total reviews</span>
      </div>

      <div className="space-y-4">
        {testimonials.map((t) => (
          <Card key={t.id} className="bg-gray-900 border-gray-800" data-testid={`testimonial-card-${t.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-white font-medium">{t.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-400">{t.source || 'Website'}</span>
                    {t.approved === true && (
                      <span className="text-xs px-2 py-0.5 rounded bg-green-900/30 text-green-400">Approved</span>
                    )}
                    {t.approved === false && (
                      <span className="text-xs px-2 py-0.5 rounded bg-red-900/30 text-red-400">Rejected</span>
                    )}
                  </div>
                  <div className="flex items-center space-x-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={14}
                        className={i < (t.rating || 5) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}
                      />
                    ))}
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">{t.content}</p>
                  {t.role && <p className="text-gray-500 text-xs mt-2">{t.role}</p>}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleApprove(t.id)}
                    className="text-green-400 hover:text-green-300 hover:bg-green-900/20"
                    title="Approve"
                    data-testid={`approve-${t.id}`}
                  >
                    <CheckCircle size={16} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleReject(t.id)}
                    className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20"
                    title="Reject"
                    data-testid={`reject-${t.id}`}
                  >
                    <XCircle size={16} />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(t.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                    title="Delete"
                    data-testid={`delete-${t.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminTestimonials;
